export type BillingInterval = "monthly" | "yearly";
export type PlanId = "personal" | "team" | "scale" | "enterprise";

export type PricingPlan = {
  id: PlanId;
  name: string;
  description: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  cta: {
    text: string;
    variant: "default" | "outline" | "secondary" | "destructive" | "ghost" | "link";
    href: string;
  };
  highlights: string[];
  isHighlighted?: boolean;
};

const currencyFormatter = new Intl.NumberFormat("en-US");

const formatNumber = (value: number) => currencyFormatter.format(value);

export const formatCurrency = (value: number) => `$${formatNumber(value)}`;

export const pricingPlans: PricingPlan[] = [
  {
    id: "personal",
    name: "Personal",
    description: "For individuals getting started with Moondream.",
    monthlyPrice: 0,
    annualPrice: null,
    cta: {
      text: "Get started",
      variant: "outline",
      href: "/login",
    },
    highlights: [
      "1 seat",
      "$5/mo free usage included",
      "2 deployed finetunes",
      "Moondream Station",
      "Discord support",
    ],
  },
  {
    id: "team",
    name: "Team",
    description: "For teams building with shared credits and support.",
    monthlyPrice: 350,
    annualPrice: 3600,
    cta: {
      text: "Start Team",
      variant: "default",
      href: "/me/create-organization",
    },
    highlights: [
      "10 seats",
      "$50/mo usage included",
      "25 deployed finetunes",
      "Moondream Station",
      "Email support\n3 business day SLA",
    ],
    isHighlighted: true,
  },
  {
    id: "scale",
    name: "Scale",
    description: "For larger organizations scaling inference and RL.",
    monthlyPrice: 950,
    annualPrice: 9600,
    cta: {
      text: "Start Scale",
      variant: "outline",
      href: "/me/create-organization",
    },
    highlights: [
      "50 seats",
      "$100/mo usage included",
      "200 deployed finetunes",
      "Moondream Station Pro",
      "Dedicated Slack\n1 business day SLA",
      "HIPAA available",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "For advanced compliance and deployment needs.",
    monthlyPrice: null,
    annualPrice: null,
    cta: {
      text: "Get started",
      variant: "outline",
      href: "/login",
    },
    highlights: [
      "Unlimited seats",
      "Custom usage",
      "Unlimited deployed finetunes",
      "Moondream Station Pro",
      "Custom-tailored support",
      "HIPAA, audit logs, and SSO"
    ],
  },
];

export const planOrder: PlanId[] = [
  "personal",
  "team",
  "scale",
  "enterprise",
];

export const getPlanPricePresentation = (
  plan: PricingPlan,
  billingInterval: BillingInterval,
) => {
  if (plan.monthlyPrice === null) {
    return {
      price: "Custom",
      subLabel: null,
      isCustom: true,
    };
  }

  if (plan.monthlyPrice === 0 && plan.annualPrice === null) {
    return {
      price: "0",
      subLabel: "No credit card required",
      isCustom: false,
    };
  }

  if (billingInterval === "yearly" && plan.annualPrice) {
    const monthlyEquivalent = Math.round(plan.annualPrice / 12);

    return {
      price: formatNumber(monthlyEquivalent),
      originalPrice: formatNumber(plan.monthlyPrice),
      subLabel: "per month, billed annually",
      isCustom: false,
    };
  }

  return {
    price: formatNumber(plan.monthlyPrice),
    subLabel: "per month",
    isCustom: false,
  };
};
